import { createFormHook, createFormHookContexts } from '@tanstack/react-form';
import type { ComponentProps } from 'react';
import { ChoiceField, DateField, TextAreaField, TextField } from './form-fields';
import { fieldError } from '@/lib/form-validation';

const { fieldContext, formContext, useFieldContext } = createFormHookContexts();

export const { useAppForm, withFieldGroup } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    TextField: FormTextField,
    TextAreaField: FormTextAreaField,
    ChoiceField: FormChoiceField,
    DateField: FormDateField,
  },
  formComponents: {},
});

type FormFieldProps<Props> = Omit<
  Props,
  'id' | 'name' | 'value' | 'defaultValue' | 'onChange' | 'onBlur'
> & { id?: string };

function FormTextField({ id, ...props }: FormFieldProps<ComponentProps<typeof TextField>>) {
  const field = useFieldContext<string>();
  return (
    <TextField
      {...props}
      id={id ?? field.name}
      name={field.name}
      value={field.state.value}
      onChange={(event) => field.handleChange(event.target.value)}
      onBlur={field.handleBlur}
      error={fieldError(field) ?? props.error}
    />
  );
}

function FormTextAreaField({ id, ...props }: FormFieldProps<ComponentProps<typeof TextAreaField>>) {
  const field = useFieldContext<string>();
  return (
    <TextAreaField
      {...props}
      id={id ?? field.name}
      name={field.name}
      value={field.state.value}
      onChange={(event) => field.handleChange(event.target.value)}
      onBlur={field.handleBlur}
      error={fieldError(field) ?? props.error}
    />
  );
}

function FormChoiceField({
  id,
  ...props
}: FormFieldProps<ComponentProps<typeof ChoiceField<string>>>) {
  const field = useFieldContext<string>();
  return (
    <ChoiceField
      {...props}
      id={id ?? field.name}
      value={field.state.value}
      onChange={field.handleChange}
      onBlur={field.handleBlur}
      error={fieldError(field) ?? props.error}
    />
  );
}

function FormDateField({ id, ...props }: FormFieldProps<ComponentProps<typeof DateField>>) {
  const field = useFieldContext<string>();
  return (
    <DateField
      {...props}
      id={id ?? field.name}
      value={field.state.value}
      onChange={field.handleChange}
      onBlur={field.handleBlur}
      error={fieldError(field) ?? props.error}
    />
  );
}
